import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Check, Sparkle, House, ChartLineUp,
  ClipboardText, CurrencyDollar, Shield, Users, Target,
  Lightbulb, Gear, ChartBar, FileText, Heart
} from "@phosphor-icons/react";

const HomeHealthServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pdgmServices = [
    {
      icon: ClipboardText,
      title: "Referral Intake & Pre-Admission Review",
      items: [
        "PDGM-focused intake review to ensure accurate clinical grouping",
        "Diagnosis sequencing and comorbidity capture",
        "Identification of high-value referral opportunities"
      ]
    },
    {
      icon: FileText,
      title: "Clinical Documentation Improvement",
      items: [
        "OASIS and documentation alignment with PDGM payment drivers",
        "Training clinicians on diagnosis coding and documentation specificity",
        "Ensuring documentation supports case mix weight and reimbursement"
      ]
    },
    {
      icon: CurrencyDollar,
      title: "Revenue & Case Mix Optimization",
      items: [
        "PDGM grouping analysis to identify missed revenue opportunities",
        "Functional impairment scoring improvement strategies",
        "Comorbidity adjustment capture"
      ]
    },
    {
      icon: ChartLineUp,
      title: "Episode Management & Utilization",
      items: [
        "Visit utilization modeling under 30-day payment periods",
        "LUPA risk monitoring and prevention strategies",
        "Care plan alignment with reimbursement structure"
      ]
    },
    {
      icon: Shield,
      title: "Quality & Compliance",
      items: [
        "PDGM-aligned QAPI metrics and monitoring",
        "ADR and medical review preparation",
        "Documentation audits for compliance with CMS guidelines"
      ]
    }
  ];

  const operationalServices = [
    "PDGM performance dashboards",
    "Intake and referral management workflow design",
    "Clinician productivity models",
    "LUPA prevention strategies",
    "Revenue cycle alignment with PDGM billing",
    "Leadership and staff education"
  ];

  const clientTypes = [
    { icon: House, label: "Home Health Agencies" },
    { icon: Heart, label: "Hospice Organizations expanding into Home Health" },
    { icon: ChartBar, label: "Private Equity-backed providers" },
    { icon: Users, label: "Hospital-based home health programs" }
  ];

  return (
    <div data-testid="home-health-services-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="section-padding bg-slate-50 relative overflow-hidden" data-testid="pdgm-hero">
        <div className="absolute inset-0 bg-mesh-gradient" />
        <div className="absolute inset-0 decorative-grid opacity-30" />
        
        <motion.div 
          className="absolute top-20 right-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="max-w-7xl mx-auto container-padding relative">
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="badge badge-accent mb-8">
              <House weight="fill" className="w-4 h-4 mr-2" />
              Home Health Services
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-slate-900 mb-8 leading-[1.05] tracking-tight">
              PDGM Optimization for{" "}
              <span className="text-gradient">Home Health Success</span>
            </h1>
            <p className="text-xl text-slate-700 leading-relaxed mb-10 font-medium">
              Our team provides strategic and operational support to home health agencies 
              navigating the complexities of the Patient-Driven Groupings Model (PDGM). 
              We focus on improving clinical documentation, referral intake processes, 
              and episode management to ensure agencies maximize reimbursement while 
              maintaining regulatory compliance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="btn-accent inline-flex items-center justify-center gap-2"
                data-testid="pdgm-cta"
              >
                Schedule PDGM Assessment
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PDGM Strategy Section */}
      <section className="section-padding bg-white" data-testid="pdgm-strategy">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">PDGM Strategy & Optimization</span>
            <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-6 tracking-tight">
              We Help Agencies Strengthen Their PDGM Performance
            </h2>
            <p className="text-lg text-slate-700 max-w-3xl mx-auto font-medium">
              Comprehensive support across all aspects of PDGM to optimize clinical 
              documentation, maximize reimbursement, and ensure compliance.
            </p>
          </motion.div>

          <div className="space-y-8">
            {pdgmServices.map((service, index) => (
              <motion.div
                key={index}
                className="bg-slate-50 rounded-3xl p-8 md:p-10 border border-slate-100 hover:border-cyan-500/30 hover:shadow-xl transition-all duration-500"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`pdgm-service-${index}`}
              >
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center">
                      <service.icon weight="fill" className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-display text-2xl text-slate-900 mb-5">{service.title}</h3>
                    <ul className="space-y-4">
                      {service.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-4">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check weight="bold" className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-slate-700 font-medium text-lg">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Operational Support Section */}
      <section className="section-padding bg-slate-900" data-testid="operational-support">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 text-cyan-400 font-semibold text-sm mb-6">
                <Gear weight="fill" className="w-4 h-4" />
                Operational & Financial Performance
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-white mb-8 tracking-tight">
                Building Sustainable PDGM Performance
              </h2>
              <p className="text-xl text-slate-300 leading-relaxed mb-10 font-medium">
                We assist agencies in building operational systems that support 
                sustainable PDGM performance and long-term financial health.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {operationalServices.map((service, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check weight="bold" className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-slate-300 font-medium">{service}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-10 border border-slate-700">
                <h3 className="font-display text-2xl text-white mb-8">Key Focus Areas</h3>
                <div className="space-y-6">
                  {[
                    { icon: ChartLineUp, label: "30-Day Episode Optimization", desc: "Maximize reimbursement per episode" },
                    { icon: Target, label: "LUPA Prevention", desc: "Reduce low utilization payment adjustments" },
                    { icon: CurrencyDollar, label: "Revenue Cycle Alignment", desc: "Billing aligned with PDGM structure" },
                    { icon: Lightbulb, label: "Staff Education", desc: "Training on PDGM documentation requirements" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <item.icon weight="duotone" className="w-6 h-6 text-cyan-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white text-lg">{item.label}</h4>
                        <p className="text-slate-400">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Support Section */}
      <section className="section-padding bg-white" data-testid="who-we-support">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">Who We Support</span>
            <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-6 tracking-tight">
              Organizations We Work With
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {clientTypes.map((client, index) => (
              <motion.div
                key={index}
                className="bg-slate-50 rounded-2xl p-8 text-center hover:shadow-xl hover:border-cyan-500/30 border border-transparent transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-5">
                  <client.icon weight="fill" className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-display font-bold text-slate-900 text-lg">{client.label}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section-padding" data-testid="pdgm-cta-section">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-32 h-32 border border-cyan-500/30 rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-cyan-500/30 rounded-full" />
        </div>
        
        <motion.div 
          className="absolute top-0 right-1/4 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="relative max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mx-auto mb-8">
              <Sparkle weight="fill" className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white mb-8 tracking-tight">
              Ready to Optimize Your PDGM Performance?
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-12 font-medium">
              Let us help you build sustainable systems that maximize reimbursement 
              while maintaining the highest standards of patient care.
            </p>
            <Link
              to="/contact"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold px-10 py-5 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/30 hover:-translate-y-1 inline-flex items-center justify-center gap-2"
              data-testid="pdgm-final-cta"
            >
              Schedule a Consultation
              <ArrowRight weight="bold" className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomeHealthServicesPage;
